import React, { useEffect, useRef, useState } from 'react';
import CardContent from '@material-ui/core/CardContent';
import {
  Card,
  Divider,
  Grid,
  Hidden,
  Link,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { Doughnut } from 'react-chartjs-2';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import 'chartjs-plugin-doughnutlabel';
import { useSelector } from 'react-redux';
import SeeAllWhite from './SeeAllWhite';
import {
  ActionTakenType,
  DocumentPriorityType,
  DocumentReview,
  DocumentTemplateType,
  SecurityPermissionLevel,
  WorkflowStatusType,
} from '../../api-client/autogenerated';
import CircularLoader from '../loader/CircularLoader';
import {
  documentIsOverdue,
  documentTypeToUrl,
  getTaskPriorityColor,
  getTaskStatusColors,
  getUserFriendlyDocumentPriority,
} from '../../scripts/utils';
import StatusPill from '../custom-components/StatusPill';
import { getProjectState } from '../../features/project/selectors';
import { useHistory } from 'react-router-dom';
import {
  getDocumentsSummaryByProjectIdAndType,
  IDocumentSummary,
} from '../../models/api/documents';
import AddDocumentDialog from '../dialogs/AddDocumentDialog';
import { Add } from '@material-ui/icons';
import { getCurrentSecurityGroup } from '../../features/security/selectors';
import { getUserState } from '../../features/user/selectors';
import { hasCompletedConstructionSetup } from '../../scripts/store-utils';
import RightArrowIconWhite from '../icons/RightArrow-icon-white';
import { useAppUtilsContext } from '../AppUtilsContext';
import { getReviewByProjectIdAndDocumentTemplateId } from '../../models/api/project';

interface DashboardDoughnutsProps {
  type:
    | DocumentTemplateType.Submittals
    | DocumentTemplateType.RequestsForInformation
    | DocumentTemplateType.Task
    | DocumentTemplateType.CaTask;
  /**
   * When true, this single Task card carries a header toggle to switch the displayed
   * data between Design Tasks and CA Tasks (replacing the two separate task cards).
   */
  combineTasks?: boolean;
  /**
   * When true (used on the Task table pages), the two side squares show task Priority
   * counts and Overdue Tasks instead of the review-performance statistics.
   */
  taskPageStats?: boolean;
  /** On the task page, clicking a doughnut slice/legend filters the table below by that status. */
  onStatusClick?: (status: string) => void;
  /**
   * On the task page, clicking a priority in the Priority square filters the table below by that
   * priority, replacing any existing priority filter (other filters are left untouched).
   */
  onPriorityClick?: (priority: string) => void;
  /**
   * taskPageStats only: additional 154x154 squares (e.g. the task page's own review-performance
   * squares) rendered in the same column as, and wrapping together with, the Priority/Overdue
   * squares -- so the ring never shrinks until every square (its own plus these) has already
   * wrapped below it.
   */
  extraSquares?: React.ReactNode;
  /**
   * taskPageStats only: the Task Manager page's own already-fetched documents, narrowed to the
   * user's "Show Tasks for Me/My Company/Everyone" selection. When provided, this replaces the
   * component's own fetch so the ring and Priority/Overdue squares reflect the same scope as the
   * table below them instead of always summarizing every task in the project.
   */
  scopedDocuments?: IDocumentSummary[];
}

const useStyles = makeStyles({
  root: {
    top: '83px',
    flexGrow: 1,
    boxShadow: 'none',
  },
  // Matches the informational squares in DashboardBidding exactly.
  squareButton: {
    height: 154,
    width: 154,
    margin: 4,
    paddingLeft: 7,
    paddingTop: 16,
    borderRadius: 8,
    border: '1px solid',
    backgroundColor: 'rgba(9,71,185,0.4)',
  },
  buttonTitle: {
    fontSize: 18,
    fontWeight: 500,
  },
  buttonDividerHorizontal: {
    marginTop: 4,
    marginRight: 8,
    backgroundColor: '#464546',
  },
  buttonDividerVertical: {
    height: 36,
    marginLeft: 4,
    marginRight: 8,
    backgroundColor: '#464546',
  },
  buttonRowNoHover: {
    display: 'flex',
    height: 28,
    paddingTop: 4,
    marginRight: 8,
  },
  // Clickable priority row on the task page — matches the hover treatment used in
  // DashboardBidding's buttonRow.
  buttonRow: {
    display: 'flex',
    height: 28,
    paddingTop: 4,
    marginRight: 8,
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: 'rgba(9,71,185,0.4)',
    },
  },
  buttonLabelNumber: {
    textAlign: 'right',
    minWidth: 26,
  },
  paper: {
    position: 'relative',
    height: '100%',
    background: 'inherit',
  },
  titleStyle: {
    background: 'linear-gradient(225deg, #00308C 0%, #002366 100%)',
    borderRadius: '4px 4px 0px 0px',
  },
  flexColumn: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
  },
  infoGrid: {
    border: 'solid 1px #EDECEC',
    minHeight: '244px',
  },
  link: {
    fontSize: 16,
    // fontStyle: 'bold',
    lineHeight: 0,
    '&:hover': {
      filter: 'brightness(0.8)',
      textDecoration: 'none',
      color: '#FFFFFF',
    },
    color: '#FFFFFF',
  },
  toggleGroup: {
    backgroundColor: 'rgba(255,255,255,0.15)',
    height: 32,
  },
  toggleButton: {
    color: '#FFFFFF',
    borderColor: 'rgba(255,255,255,0.5)',
    padding: '2px 14px',
    textTransform: 'none',
    fontWeight: 500,
    '&.Mui-selected': {
      backgroundColor: '#FFFFFF',
      color: '#0947B9',
      '&:hover': {
        backgroundColor: '#FFFFFF',
      },
    },
  },
});

export default function DashboardDoughnut(props: DashboardDoughnutsProps) {
  const classes = useStyles();
  const history = useHistory();

  const user = useSelector(getUserState);
  const project = useSelector(getProjectState);
  const securityGroup = useSelector(getCurrentSecurityGroup);
  const {
    combineTasks,
    taskPageStats,
    onStatusClick,
    onPriorityClick,
    extraSquares,
    scopedDocuments,
  } = props;

  // The combined Task card toggles between Design and CA tasks; everything below keys
  // off `type`, so switching the toggle re-fetches and re-renders for the chosen type.
  const [taskType, setTaskType] = useState<DocumentTemplateType.Task | DocumentTemplateType.CaTask>(
    DocumentTemplateType.Task,
  );
  const type = combineTasks ? taskType : props.type;

  const {
    documentTemplates: { getTemplateId },
  } = useAppUtilsContext();

  const chartRef = useRef<Doughnut>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [fetchedDocuments, setFetchedDocuments] = useState<IDocumentSummary[]>([]);
  // scopedDocuments (when passed) is already the source of truth for the Task Manager table
  // itself, so use it directly instead of this component's own fetch below.
  const documents = scopedDocuments ?? fetchedDocuments;
  const [data, setData] = useState([0, 0, 0, 0, 0, 0, 0]);

  const [canCreateTasks, setCanCreateTasks] = useState(false);
  const [hasReviewPermission, setHasReviewPermission] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [reviewStatistics, setReviewStatistics] = useState<DocumentReview>();
  const anchor = useRef(null);

  const hasConstructionSetupPermission =
    user.isSiteAdmin ||
    user.adminOfSubscriberId === project?.subscriberId ||
    !!securityGroup?.constructionManagementPermission;

  const isMobile = useMediaQuery('(max-width:600px)');
  const isBelow1150 = useMediaQuery('(max-width:1150px)');

  const fetchDocuments = async () => {
    // The caller already fetched (and scoped) these; skip the redundant network round-trip.
    if (scopedDocuments) return;
    if (!project) return;
    try {
      setIsLoading(true);
      let results: IDocumentSummary[] = [];
      if (type === DocumentTemplateType.Submittals) {
        results.push(
          ...(
            await Promise.all([
              getDocumentsSummaryByProjectIdAndType(project.id, DocumentTemplateType.Submittals),
              getDocumentsSummaryByProjectIdAndType(
                project.id,
                DocumentTemplateType.SubmittalPackages,
              ),
            ])
          ).flat(),
        );
      } else {
        results.push(...(await getDocumentsSummaryByProjectIdAndType(project.id, type)));
      }
      setFetchedDocuments(results);
    } catch {
      setFetchedDocuments([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDocumentReview = async (templateId: string) => {
    try {
      const res = await getReviewByProjectIdAndDocumentTemplateId(project!.id, templateId);
      setReviewStatistics(res);
    } catch {
      setReviewStatistics({
        averageDeltaDays: 0,
        averageReviewDays: 0,
      });
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [project, type]);

  useEffect(() => {
    if (!type || !project) {
      setHasReviewPermission(false);
      return;
    }

    const templateId = getTemplateId(type);

    const permission = securityGroup?.securityGroupDocumentTemplateList?.find(
      (sgdt) => sgdt.documentTemplateId === templateId,
    )?.securityPermissionLevel;

    const canReview =
      user.isSiteAdmin ||
      user.adminOfSubscriberId === project.subscriberId ||
      !!(permission && permission >= SecurityPermissionLevel.NUMBER_3);

    setHasReviewPermission(canReview);

    if (canReview) {
      fetchDocumentReview(templateId);
    }
  }, [securityGroup, type, project, user]);

  useEffect(() => {
    if ((type === DocumentTemplateType.Task || type === DocumentTemplateType.CaTask) && project) {
      const taskTemplateId = getTemplateId(type);

      const taskPermission = securityGroup?.securityGroupDocumentTemplateList?.find(
        (sgdt) => sgdt.documentTemplateId === taskTemplateId,
      )?.securityPermissionLevel;
      setCanCreateTasks(
        user.isSiteAdmin ||
          user.adminOfSubscriberId === project.subscriberId ||
          !!(taskPermission && taskPermission >= SecurityPermissionLevel.NUMBER_3),
      );
    }
  }, [securityGroup, type, project, user]);

  useEffect(() => {
    const tempData = [0, 0, 0, 0, 0, 0, 0];
    documents.forEach((document) => {
      if (type === DocumentTemplateType.RequestsForInformation) {
        switch (document.workflowStatus) {
          case WorkflowStatusType.SubmittedForReview:
            if (documentIsOverdue(document)) tempData[5] += 1;
            tempData[0] += 1;
            break;
          case WorkflowStatusType.ArchitectUploaded:
          case WorkflowStatusType.UnderReview:
            if (documentIsOverdue(document)) tempData[5] += 1;
            tempData[1] += 1;
            break;
          case WorkflowStatusType.ReviewComplete:
            tempData[2] += 1;
            break;
          case WorkflowStatusType.Resolved:
            tempData[3] += 1;
            break;
          case WorkflowStatusType.Resubmitted:
            tempData[4] += 1;
            break;
        }
      } else if (type === DocumentTemplateType.Submittals) {
        if (document.submittalPackageDocumentId) return;
        if (document.actionTaken === ActionTakenType.ReviseAndResubmit) tempData[4] += 1;
        switch (document.workflowStatus) {
          case WorkflowStatusType.Initial:
          case WorkflowStatusType.SubcontractorUploaded:
          case WorkflowStatusType.ReadyForSubmissionToArchitect:
          case WorkflowStatusType.GeneralContractorUploaded:
            if (documentIsOverdue(document, true)) tempData[5] += 1;
            tempData[0] += 1;
            break;
          case WorkflowStatusType.SubmittedForReview:
            if (documentIsOverdue(document, true)) tempData[5] += 1;
            tempData[1] += 1;
            break;
          case WorkflowStatusType.UnderReview:
          case WorkflowStatusType.ArchitectUploaded:
            if (documentIsOverdue(document, true)) tempData[5] += 1;
            tempData[2] += 1;
            break;
          case WorkflowStatusType.Completed:
            tempData[3] += 1;
            break;
        }
      } else {
        if (document.priority === DocumentPriorityType.ForInformationOnly) {
          tempData[0] += 1;
          return;
        }
        switch (document.workflowStatus) {
          case WorkflowStatusType.Initial:
          case WorkflowStatusType.SubcontractorUploaded:
          case WorkflowStatusType.ReadyForSubmissionToArchitect:
          case WorkflowStatusType.GeneralContractorUploaded:
          case WorkflowStatusType.SubmittedForReview:
            if (documentIsOverdue(document)) tempData[6] += 1;
            tempData[1] += 1;
            break;
          case WorkflowStatusType.UnderReview:
          case WorkflowStatusType.ArchitectUploaded:
            if (documentIsOverdue(document)) tempData[6] += 1;
            tempData[2] += 1;
            break;
          case WorkflowStatusType.ReviewComplete:
            tempData[3] += 1;
            break;
          case WorkflowStatusType.Resolved:
            tempData[4] += 1;
            break;
          case WorkflowStatusType.Resubmitted:
            tempData[5] += 1;
            break;
        }
      }
    });
    setData(tempData);
  }, [documents]);

  const getLabels = () => {
    switch (type) {
      case DocumentTemplateType.Submittals:
        return getSubmittalLabels();
      case DocumentTemplateType.RequestsForInformation:
        return getRfiLabels();
      case DocumentTemplateType.Task:
      case DocumentTemplateType.CaTask:
        return getTaskLabels();
    }
  };

  const getSubmittalLabels = () => [
    `Awaiting Submission ${data[0]}`,
    `Submitted for Review ${data[1]}`,
    `Under Review ${data[2]}`,
    `Completed ${data[3]}`,
    `Revise & Resubmit ${data[4]}`,
    `Overdue ${data[5]}`,
  ];

  const getRfiLabels = () => [
    `Submitted for Review ${data[0]}`,
    `Under Review ${data[1]}`,
    `Review Complete ${data[2]}`,
    `Resolved ${data[3]}`,
    `Resubmitted ${data[4]}`,
    `Overdue ${data[5]}`,
  ];

  const getTaskLabels = () => [
    `For Information Only ${data[0]}`,
    `Not Started ${data[1]}`,
    `In Progress ${data[2]}`,
    `Completed ${data[3]}`,
    `Accepted ${data[4]}`,
    `Resubmitted ${data[5]}`,
    `Overdue ${data[6]}`,
  ];

  const getStatusFromLabel = (label: string): string => {
    switch (label.slice(0, label.lastIndexOf(' '))) {
      default:
      case 'Awaiting Submission':
        return WorkflowStatusType.Initial;

      case 'Submitted for Review':
      case 'Not Started':
        return WorkflowStatusType.SubmittedForReview;

      case 'Under Review':
      case 'In Progress':
        return WorkflowStatusType.UnderReview;

      case 'Resolved':
      case 'Accepted':
        return WorkflowStatusType.Resolved;

      case 'Resubmitted':
        return WorkflowStatusType.Resubmitted;

      case 'Review Complete':
        return WorkflowStatusType.ReviewComplete;

      case 'Completed':
        if (type === DocumentTemplateType.Task || type === DocumentTemplateType.CaTask)
          return WorkflowStatusType.ReviewComplete;
        return WorkflowStatusType.Completed;

      case 'Revise & Resubmit':
        return ActionTakenType.ReviseAndResubmit;

      case 'Overdue':
        return 'overdue';

      case 'For Information Only':
        return DocumentPriorityType.ForInformationOnly;
    }
  };

  // Derives each slice's color from the same palette the document table uses for its status
  // pill (getTaskStatusColors), via the label -> status mapping getStatusFromLabel already
  // maintains above, so the doughnut can never drift out of sync with the table again.
  const getColorForLabel = (label: string): string => {
    const key = getStatusFromLabel(label);
    // Overdue isn't a status getTaskStatusColors knows about — on the document table it's
    // always a separate, hardcoded red pill alongside the main status.
    if (key === 'overdue') return '#E2445C';
    // For Information Only is a priority, not a status — getTaskStatusColors only special-cases
    // it via the priority argument.
    if (key === DocumentPriorityType.ForInformationOnly) {
      return getTaskStatusColors(undefined, key).bg;
    }
    return getTaskStatusColors(key).bg;
  };

  const getColors = () => getLabels()?.map(getColorForLabel);

  const getDocumentCount = () => {
    if (type === DocumentTemplateType.Submittals) {
      return documents.filter((d) => !d.submittalPackageDocumentId).length;
    } else {
      return documents.length;
    }
  };

  const pieData = {
    labels: getLabels(),
    datasets: [
      {
        data:
          type === DocumentTemplateType.Task || type === DocumentTemplateType.CaTask
            ? data
            : data.slice(0, 6),
        backgroundColor: getColors(),
        // hoverBackgroundColor: ['#C4C4C4', '#C4C4C4', '#C4C4C4'],
      },
    ],
    // text: `${documents.length} Total`,
  };

  const emptyData = {
    labels: getLabels(),
    datasets: [
      {
        data: [1, 0, 0, 0, 0, 0, 0],
        backgroundColor: ['#EDECEC'],
      },
    ],
  };

  const plugins = {
    doughnutlabel: {
      labels: [
        {
          text: getDocumentCount(),
          font: {
            size: 44,
            lineHeight: '52px',
          },
        },
        {
          text: 'total',
          font: {
            size: 26,
            lineHeight: '30px',
          },
        },
      ],
    },
  };

  const doughnutOptions =
    documents.length === 0
      ? {
          animation: { duration: 0 },
          maintainAspectRatio: false,
          responsive: true,
          plugins,
          events: [],
        }
      : {
          animation: { duration: 0 },
          maintainAspectRatio: false,
          responsive: true,
          plugins,
          tooltips: {
            callbacks: {
              label: (tooltipItem: { index: number }, data: { labels: string[] }) => {
                return data.labels[tooltipItem.index];
              },
            },
          },
          hover: {
            onHover: (e: any, other: any) => {
              e.target.style.cursor = other.length ? 'pointer' : 'default';
            },
          },
          onClick: (e: any, items: any[]) => {
            if (items.length > 0) {
              const status = getStatusFromLabel(items[0]._view.label);
              // On the task page the card lives above the table, so filter it in place
              // rather than navigating.
              if (taskPageStats && onStatusClick) {
                onStatusClick(status);
                return;
              }
              setTimeout(() => {
                chartRef.current?.chartInstance.destroy();
                history.push(`/main/projects/${project!.id}/documents/${documentTypeToUrl[type]}`, {
                  status,
                });
              }, 100);
            }
          },
        };

  const legendOptions = {
    onClick: (event: MouseEvent, item: { text: string }) => {
      const status = getStatusFromLabel(item.text);
      if (taskPageStats && onStatusClick) {
        onStatusClick(status);
        return;
      }
      history.push(`/main/projects/${project!.id}/documents/${documentTypeToUrl[type]}`, {
        status,
      });
    },
    onHover: (e: any) => {
      e.target.style.cursor = 'pointer';
    },
  };

  const getTitle = () => {
    switch (type) {
      case DocumentTemplateType.Submittals:
        return 'Submittals';
      case DocumentTemplateType.RequestsForInformation:
        return 'RFIs';
      case DocumentTemplateType.Task:
        return combineTasks ? 'Task Manager' : 'Design - Task Manager';
      case DocumentTemplateType.CaTask:
        return combineTasks ? 'Task Manager' : 'CA - Task Manager';
    }
  };

  const handleConstructionSetupLink = () => {
    history.push(`/main/projects/${project?.id}/const-mgmt`);
  };

  const getButton = () => {
    if (
      type === DocumentTemplateType.Task ||
      type === DocumentTemplateType.CaTask ||
      hasCompletedConstructionSetup()
    )
      return (
        <SeeAllWhite link={`/main/projects/${project!.id}/documents/${documentTypeToUrl[type]}`} />
      );

    if (hasConstructionSetupPermission)
      return (
        <Link onClick={handleConstructionSetupLink} className={classes.link}>
          Construction Setup <RightArrowIconWhite />
        </Link>
      );

    return null;
  };

  const priorityCounts = {
    high: documents.filter((d) => d.priority === DocumentPriorityType.High).length,
    medium: documents.filter((d) => d.priority === DocumentPriorityType.Medium).length,
    low: documents.filter((d) => d.priority === DocumentPriorityType.Low).length,
  };
  const overdueCount = documents.filter((d) => documentIsOverdue(d)).length;

  const taskStatSquares = (
    <>
      <div className={classes.squareButton} style={{ color: '#464546' }}>
        <Typography className={classes.buttonTitle}>Priority</Typography>
        <Divider className={classes.buttonDividerHorizontal} />
        {[
          { priority: DocumentPriorityType.High, count: priorityCounts.high },
          { priority: DocumentPriorityType.Medium, count: priorityCounts.medium },
          { priority: DocumentPriorityType.Low, count: priorityCounts.low },
        ].map((row) => (
          <div
            key={row.priority}
            className={onPriorityClick ? classes.buttonRow : classes.buttonRowNoHover}
            style={{ alignItems: 'center', marginBottom: 8, marginTop: 2 }}
            onClick={onPriorityClick ? () => onPriorityClick(row.priority) : undefined}
          >
            <Typography className={classes.buttonLabelNumber}>{row.count}</Typography>
            <Divider orientation="vertical" className={classes.buttonDividerVertical} />
            <StatusPill backgroundColor={getTaskPriorityColor(row.priority)}>
              {getUserFriendlyDocumentPriority(row.priority)}
            </StatusPill>
          </div>
        ))}
      </div>
      <div
        style={{
          width: 154,
          height: 154,
          backgroundColor: 'rgba(9,71,185,0.4)',
          borderRadius: 8,
          display: 'grid',
          placeItems: 'center',
          padding: 10,
          margin: 4,
          boxSizing: 'border-box',
          color: '#464546',
          border: '1px solid',
        }}
      >
        <div style={{ textAlign: 'center', maxWidth: '100%' }}>
          <div style={{ marginBottom: 20 }}>
            <StatusPill backgroundColor="#E2445C">Overdue</StatusPill>
          </div>
          <div
            style={{
              fontSize: 35,
              marginBottom: 15,
              fontWeight: 400,
              color: overdueCount > 0 ? '#ED3F26' : undefined,
            }}
          >
            {overdueCount}
          </div>
          <div style={{ fontSize: 14 }}>total</div>
        </div>
      </div>
    </>
  );

  // The ring + legend, shared verbatim between the taskPageStats flex layout and the regular
  // dashboard-card Grid layout below.
  const ringElement = (
    <Paper className={classes.paper}>
      {canCreateTasks && !taskPageStats && (
        <Button
          ref={anchor}
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => setCreateDialogOpen(true)}
          style={{ marginLeft: 16, marginBottom: 16 }}
        >
          Create
        </Button>
      )}
      <Hidden smDown>
        <div style={{ width: '99%' }}>
          <Doughnut
            ref={chartRef}
            data={documents.length === 0 ? emptyData : pieData}
            width={300}
            height={300}
            legend={{ position: 'right', ...legendOptions }}
            options={doughnutOptions}
          />
        </div>
      </Hidden>
      <Hidden mdUp>
        <div style={{ width: '99%' }}>
          <Doughnut
            ref={chartRef}
            data={documents.length === 0 ? emptyData : pieData}
            width={300}
            height={300}
            legend={{ position: 'bottom', ...legendOptions }}
            options={doughnutOptions}
          />
        </div>
      </Hidden>
    </Paper>
  );

  return (
    <Card
      className={classes.root}
      style={taskPageStats ? { backgroundColor: '#FAFAFA' } : undefined}
    >
      {/* The task-page cards omit the header (title + See All); the page already titles them. */}
      {!taskPageStats && (
        <CardContent className={classes.titleStyle}>
          <Grid container justify="space-between" alignItems="center" spacing={1}>
            <Grid item>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <h2
                  className="h2"
                  style={{
                    textAlign: 'left',
                    color: '#FFFFFF',
                    margin: 0,
                  }}
                >
                  {getTitle()}
                </h2>
                {combineTasks && (
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={taskType}
                    onChange={(_e, value) => {
                      if (value) setTaskType(value);
                    }}
                    className={classes.toggleGroup}
                  >
                    <ToggleButton
                      value={DocumentTemplateType.Task}
                      className={classes.toggleButton}
                    >
                      Design
                    </ToggleButton>
                    <ToggleButton
                      value={DocumentTemplateType.CaTask}
                      className={classes.toggleButton}
                    >
                      CA
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              </div>
            </Grid>

            <Grid item>
              <Button style={{ padding: '0px' }}>{getButton()}</Button>
            </Grid>
          </Grid>
        </CardContent>
      )}

      <CardContent
        className={classes.flexColumn}
        style={{ paddingLeft: '0px', paddingRight: '0px', paddingBottom: '0px' }}
      >
        {isLoading ? (
          <div
            style={{
              position: 'relative',
              top: '50%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 50,
            }}
          >
            <CircularLoader />
          </div>
        ) : taskPageStats ? (
          // Content-based flexbox instead of MUI Grid's xs/sm viewport breakpoints: those trigger
          // off the browser window's width, not this card's actual available width, so the ring
          // could resize independently of whether these squares still fit beside it.
          //
          // flexBasis is a fixed px number, not 'auto': this ring's legend has no
          // fixedLegendWidthPlugin (unlike MyTasksBreakdownChart), so Chart.js's "responsive"
          // canvas has no real preferred size of its own -- it just renders to fill whatever
          // width it's handed. 'auto' content-based sizing resolves to that same
          // fill-whatever-you-give-me behavior, so the ring shrank continuously as the row
          // narrowed instead of holding size and letting the squares wrap first. A fixed
          // flexBasis gives it an actual floor for the flex-wrap algorithm to wrap around:
          // flexGrow: 0 keeps it from stretching past that floor when there's extra room, and
          // flexShrink: 1 + minWidth: 0 still let it shrink gracefully once flexWrap has already
          // moved the squares column -- which combines the Priority/Overdue squares with any
          // extraSquares so all of them wrap together -- to its own line below.
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 20,
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
            }}
          >
            <div style={{ padding: 16, flexGrow: 0, flexShrink: 1, flexBasis: 500, minWidth: 0 }}>
              {ringElement}
            </div>
            {/* One flex item (wraps below the ring as a unit, same as before) that internally
                lays its two square groups out side by side -- Priority/Overdue, then
                extraSquares -- so at wide-enough widths extraSquares reads as further right,
                not just further down the same column. flexWrap here lets that inner pair itself
                wrap (one under the other) once the row narrows past both fitting; maxWidth:
                '100%' keeps it from sizing to its full unwrapped width and overflowing before
                that wrap can happen. */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 20,
                flexShrink: 0,
                maxWidth: '100%',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {taskStatSquares}
              </div>
              {extraSquares && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  {extraSquares}
                </div>
              )}
            </div>
          </div>
        ) : (
          <Grid container justify="center" spacing={1} alignItems="stretch">
            <Grid
              item
              xs={12}
              sm={8}
              style={{
                padding: canCreateTasks ? '0 16px 16px 16px' : '16px',
              }}
            >
              {ringElement}
            </Grid>
            <Grid item xs={12} sm={4} style={{ display: 'flex', alignItems: 'center' }}>
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: isMobile ? 'row' : 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 200,
                  boxSizing: 'border-box',
                  color: '#464546',
                  gap: 10,
                }}
              >
                {hasReviewPermission && reviewStatistics ? (
                  <>
                    <Tooltip title="Not shown to GC" arrow placement="left">
                      <div
                        style={{
                          width: 154,
                          height: 154,
                          backgroundColor: 'rgba(9,71,185,0.4)',
                          borderRadius: 8,
                          display: 'grid',
                          placeItems: 'center',
                          padding: 10,
                          boxSizing: 'border-box',
                          color: '#464546',
                          border: '1px solid',
                        }}
                      >
                        <div style={{ textAlign: 'center', maxWidth: '100%' }}>
                          <div style={{ marginBottom: 10, fontSize: 14 }}>
                            Average Review Completion
                          </div>
                          <div style={{ fontSize: 35, marginBottom: 8, fontWeight: 400 }}>
                            {reviewStatistics.averageReviewDays === 0
                              ? 'N/A'
                              : reviewStatistics.averageReviewDays.toFixed(1)}
                            <span style={{ fontSize: 14, fontWeight: 500 }}> days</span>
                          </div>
                          {type !== DocumentTemplateType.Task &&
                            type !== DocumentTemplateType.CaTask && (
                              <div style={{ fontSize: 14 }}>
                                Target=
                                {type === DocumentTemplateType.RequestsForInformation
                                  ? project!.rfiReviewPeriod || project!.documentReviewPeriod
                                  : project!.documentReviewPeriod}{' '}
                                days
                              </div>
                            )}
                        </div>
                      </div>
                    </Tooltip>

                    <Tooltip title="Not Shown to GC" arrow placement="left">
                      <div
                        style={{
                          width: 154,
                          height: 154,
                          backgroundColor: 'rgba(9,71,185,0.4)',
                          borderRadius: 8,
                          display: 'grid',
                          placeItems: 'center',
                          padding: 10,
                          boxSizing: 'border-box',
                          color: '#464546',
                          marginBottom: isMobile ? 0 : 5,
                          border: '1px solid',
                        }}
                      >
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ marginBottom: 20, fontSize: 14 }}>On-time Performance</div>
                          {reviewStatistics.averageDeltaDays === 0 ? (
                            <div
                              style={{
                                fontSize: 28,
                                color: '#464546',
                                fontWeight: 400,
                                marginBottom: 15,
                              }}
                            >
                              On time
                            </div>
                          ) : (
                            <>
                              <div
                                style={{
                                  fontSize: 35,
                                  fontWeight: 400,
                                  marginBottom: 15,
                                  color:
                                    reviewStatistics.averageDeltaDays < 0 ? '#15803d' : '#b91c1c',
                                }}
                              >
                                {Math.abs(reviewStatistics.averageDeltaDays ?? 0).toFixed(1)}
                              </div>
                              <div
                                style={{
                                  fontSize: 14,
                                  color:
                                    reviewStatistics.averageDeltaDays < 0 ? '#15803d' : '#b91c1c',
                                }}
                              >
                                days {reviewStatistics.averageDeltaDays < 0 ? 'early' : 'late'}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </Tooltip>
                  </>
                ) : null}
              </div>
            </Grid>
          </Grid>
        )}
      </CardContent>
      {canCreateTasks && (
        <AddDocumentDialog
          dialogOpen={createDialogOpen}
          handleClose={() => setCreateDialogOpen(false)}
          anchor={anchor.current}
          dashboardDocumentType={type}
        />
      )}
    </Card>
  );
}
